import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Button,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeliveryDining from "@mui/icons-material/DeliveryDining";
import CheckCircle from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningIcon from "@mui/icons-material/Warning";
import { differenceInMinutes, isSameDay } from "date-fns";
import { GridColDef } from "@mui/x-data-grid";
import { PremiumDataGrid } from "../components/PremiumDataGrid";

import { useCollection } from "../hooks/useCollection";
import { useConfig } from "../hooks/useConfig";
import ItemDialog from "../components/ItemDialog";
import { useAuth } from "../hooks/useAuth";
import { Order } from "../interfaces/Order";
import { Neighborhood } from "../interfaces/Neighborhood";
import { Users } from "../interfaces/Users";

/* =======================
   PAGE
======================= */

export default function DeliveryPage() {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const [tabValue, setTabValue] = useState(0);

  /* 🔹 Barrios */
  const {
    data: neighborhoods,
    createItem: createNeighborhood,
    updateItem: updateNeighborhood,
    deleteItem: deleteNeighborhood,
  } = useCollection<Neighborhood>(restaurantId!, "neighborhoods");

  /* 🔹 Usuarios y Órdenes */
  const { data: users } = useCollection<Users>(restaurantId!, "users");
  const { data: orders } = useCollection<Order>(restaurantId!, "orders");

  const { canCreate, canEdit, canDelete } = useAuth();
  const { config } = useConfig(restaurantId);

  /* Estados de Modal de Barrio */
  const [openNeighborhoodModal, setOpenNeighborhoodModal] = useState(false);
  const [currentNeighborhood, setCurrentNeighborhood] = useState<Neighborhood | null>(null);

  /* Estados de Productividad */
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<Users | null>(null);

  const getOrderDate = (o: any) => {
    const d = o.date || o.createdAt;
    if (!d) return new Date();
    return d.toDate ? d.toDate() : new Date(d);
  };


  /* =======================
     LOGIC
  ======================= */
  const domiciliarios = users.filter((u) => u.role === "domiciliario");

  const getActiveOrdersForDriver = (driverId: string) => {
    return orders.filter(
      (o) =>
        o.deliveryDriverId === driverId &&
        (o.state === "enCamino" || o.state === "esperando_domiciliario")
    );
  };

  const getCompletedOrdersToday = (driverId: string) => {
    return orders.filter(
      (o) =>
        o.deliveryDriverId === driverId &&
        (o.state === "entregado" || o.state === "completada") &&
        isSameDay(getOrderDate(o), new Date())
    );
  };


  /* =======================
   TABLE COLUMNS (Barrios)
======================= */

  const neighborhoodColumns: GridColDef[] = [
    { field: "name", headerName: "Nombre del Barrio", flex: 2 },
    {
      field: "price",
      headerName: "Precio del Domicilio",
      flex: 1,
      renderCell: (p) => `$${p.value?.toLocaleString()}`,
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 150,
      renderCell: (p) => (
        <Stack direction="row">
          {canEdit && (
            <IconButton
              onClick={() => {
                setCurrentNeighborhood(p.row);
                setOpenNeighborhoodModal(true);
              }}
            >
              <EditIcon />
            </IconButton>
          )}
          {canDelete && (
            <IconButton color="error" onClick={() => deleteNeighborhood(p.row.id)}>
              <DeleteIcon />
            </IconButton>
          )}
        </Stack>
      ),
    },
  ];

  /* =======================
     UI
  ======================= */

  return (
    <>
      <Typography variant="h4" mb={2} fontWeight={800} color="primary.main">
        🚚 Gestión de Delivery
      </Typography>

      <Tabs
        value={tabValue}
        onChange={(e, v) => setTabValue(v)}
        sx={{ mb: 3 }}
        textColor="primary"
        indicatorColor="primary"
      >
        <Tab label="🏘️ Barrios y Tarifas" />
        <Tab label="🛵 Estado de Domiciliarios" />
      </Tabs>

      {tabValue === 0 && (
        <>
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Typography fontWeight={600}>Lista de Barrios</Typography>
            {canCreate && (
              <Button
                variant="contained"
                onClick={() => {
                  setCurrentNeighborhood(null);
                  setOpenNeighborhoodModal(true);
                }}
              >
                Agregar Barrio
              </Button>
            )}
          </Stack>

          <Box sx={{ height: 420 }}>
            <PremiumDataGrid
              rows={neighborhoods}
              columns={neighborhoodColumns}
              getRowId={(r) => r.id!}
            />
          </Box>
        </>
      )}

      {tabValue === 1 && (
        <Grid container spacing={2}>
          {domiciliarios.length === 0 ? (
            <Grid>
              <Typography color="text.secondary">No hay domiciliarios registrados.</Typography>
            </Grid>
          ) : (
            domiciliarios.map((driver) => {
              const driverId = driver.uid || (driver as any).id;
              const activeOrders = getActiveOrdersForDriver(driverId);
              const isFree = activeOrders.length === 0;
              const driverPhone = (driver as any).phone || (driver as any).phoneNumber || (driver as any).telefono;
              
              let isDelayed = false;
              if (!isFree) {
                const oldestOrder = [...activeOrders].sort((a, b) => getOrderDate(a).getTime() - getOrderDate(b).getTime())[0];
                if (oldestOrder) {
                  const minutes = differenceInMinutes(new Date(), getOrderDate(oldestOrder));
                  isDelayed = minutes >= config.deliveryDelayThreshold;
                }
              }

              return (
                <Grid key={driverId}>
                  <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1, p: 2 }}>
                      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                        <Avatar src={driver.perfil} sx={{ width: 56, height: 56, borderRadius: 2 }} />
                        <Box flex={1}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle1" fontWeight={800}>
                              {driver.name || "Sin Nombre"}
                            </Typography>
                            <IconButton 
                              size="small" 
                              onClick={() => { setSelectedDriver(driver); setStatsModalOpen(true); }}
                              sx={{ bgcolor: '#f1f5f9' }}
                            >
                              <VisibilityIcon fontSize="small" color="primary" />
                            </IconButton>
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {driverPhone || driver.email || "Sin contacto"}
                          </Typography>
                          <Box mt={0.5}>
                            {isFree ? (
                              <Chip
                                label="Libre"
                                size="small"
                                color="success"
                                icon={<CheckCircle />}
                                sx={{ fontWeight: 700 }}
                              />
                            ) : (
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Chip
                                  label={`En Ruta: ${activeOrders.length} pedido(s)`}
                                  size="small"
                                  color="info"
                                  icon={<DeliveryDining />}
                                  sx={{ fontWeight: 700 }}
                                />
                                <Chip 
                                  label={isDelayed ? "Demorado" : "A Tiempo"} 
                                  size="small" 
                                  color={isDelayed ? "error" : "success"} 
                                  icon={isDelayed ? <WarningIcon /> : <CheckCircle />} 
                                  sx={{ fontWeight: 700 }} 
                                />
                              </Stack>
                            )}
                          </Box>
                        </Box>
                      </Stack>

                      {/* Órdenes Asignadas */}
                      {!isFree && (
                        <Box sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 2, mb: 2 }}>
                          <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            PEDIDOS ACTUALES:
                          </Typography>
                          <Stack spacing={1}>
                            {activeOrders.map((o) => (
                              <Box key={o.id} sx={{ bgcolor: '#fff', p: 1, borderRadius: 1, border: '1px solid #e2e8f0' }}>
                                <Typography variant="caption" fontWeight={800} color="primary.main">
                                  #{o.supportCode || (o.id ? o.id.slice(-4).toUpperCase() : "")} - {o.name || "Cliente"}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                  📍 {o.address || o.tableNumber || "Sin dirección"}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {/* Acciones */}
                      <Stack direction="row" spacing={1} mt="auto">
                        {driverPhone && (
                          <a
                            href={`https://wa.me/${driverPhone.replace(/\D/g, '')}?text=${encodeURIComponent("Hola, necesitamos hablar contigo sobre los pedidos en ruta.")}`}
                            target="_blank" rel="noopener noreferrer"
                            style={{
                              display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
                              backgroundColor: '#25D366', borderRadius: '24px', textDecoration: 'none',
                              color: '#fff', fontSize: '0.8rem', fontWeight: 700, width: '100%', justifyContent: 'center'
                            }}
                          >
                            <img src="https://img.icons8.com/color/48/whatsapp--v1.png" alt="WhatsApp" style={{ width: '18px', height: '18px', filter: 'brightness(0) invert(1)' }} />
                            WhatsApp
                          </a>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })
          )}
        </Grid>
      )}

      {/* ================= MODALES ================= */}

      {/* Modal Barrio */}
      <ItemDialog<Neighborhood>
        open={openNeighborhoodModal}
        onClose={() => setOpenNeighborhoodModal(false)}
        initial={
          currentNeighborhood || {
            name: "",
            price: 0,
          }
        }
        fields={[
          { key: "name", label: "Nombre del Barrio" },
          { key: "price", label: "Precio del Domicilio", type: "number" },
        ]}
        onSubmit={async (values) => {
          // Parse price to number just in case ItemDialog returns string
          const processedValues = {
            ...values,
            price: Number(values.price),
          };

          currentNeighborhood?.id
            ? updateNeighborhood(currentNeighborhood.id, processedValues)
            : createNeighborhood(processedValues);
          setOpenNeighborhoodModal(false);
        }}
      />

      {/* Modal Productividad */}
      <Dialog open={statsModalOpen} onClose={() => setStatsModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight={800}>Productividad Diaria 👁️</Typography>
          {selectedDriver && (
            <Typography variant="subtitle2" color="text.secondary">
              {selectedDriver.name || "Sin Nombre"}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedDriver ? (() => {
            const driverId = selectedDriver.uid || (selectedDriver as any).id;
            const completedOrders = getCompletedOrdersToday(driverId);
            
            if (completedOrders.length === 0) {
              return <Typography color="text.secondary">No ha entregado pedidos el día de hoy.</Typography>;
            }
            
            return (
              <Box>
                <Box mb={2} p={2} bgcolor="#f8fafc" borderRadius={2} textAlign="center">
                  <Typography variant="h3" fontWeight={900} color="primary.main">
                    {completedOrders.length}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={700} color="text.secondary">
                    Pedidos entregados hoy
                  </Typography>
                </Box>
                <Typography variant="subtitle2" fontWeight={800} mb={1}>Historial de hoy:</Typography>
                <List sx={{ bgcolor: 'background.paper', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                  {completedOrders.map((o) => (
                    <ListItem key={o.id} divider>
                      <ListItemText 
                        primary={`#${o.supportCode || (o.id ? o.id.slice(-4).toUpperCase() : "")} - ${o.name || "Cliente"}`}
                        secondary={o.address || o.tableNumber || "Sin dirección"}
                        primaryTypographyProps={{ fontWeight: 700, variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            );
          })() : null}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setStatsModalOpen(false)} variant="contained" color="primary">Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
